import { formatDollars } from '@fruitful/fruitils';
import {
    buildInvestmentWaterfall,
    DEFAULT_MEMBER_AGE,
    DEFAULT_PROJECTION_YEARS,
    type ExistingIraAccount,
    initIntakeInvestment,
    INVESTMENT_RETURN_PERCENT,
    type InvestmentWaterfallAccount,
    type InvestmentWaterfallResult,
    MONTHLY_CONTRIBUTION_STEP_CENTS,
    resolveDefaultAnnualContributionCents,
    toPersistedInvestmentWaterfall,
} from '@fruitful/money-map-simulator';
import React, { useCallback, useEffect, useMemo } from 'react';
import {
    BodyMediumRegular,
    BodySmallEmphasized,
    BodySmallRegular,
    FontFamilyName,
    FruitfulLogoIcon,
    H5Emphasized,
    Icon,
    LabelRegular,
    Shadows,
    Slider,
    SuperscriptRegular,
    Text,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { ConversationalMMAnalyticsEvent } from '../analytics/events';
import { useConversationalMMContext } from '../context/ConversationalMMContextBase';
import {
    Header,
    InvestingDisclosures,
    LinearGradientBackgroundCard,
} from './shared';
import { SlideNextButton } from './SlideNextButton';

const DEFAULT_MAX_MONTHLY_CENTS = 500_000;

const ROUND_TO_CENTS = 100_000;

function computeMaxMonthlyCents(annualCapacityCents: number | null): number {
    if (annualCapacityCents == null || annualCapacityCents <= 0)
        return DEFAULT_MAX_MONTHLY_CENTS;
    const monthlyCents = Math.round(annualCapacityCents / 12);
    const headroom =
        Math.ceil((monthlyCents * 2) / ROUND_TO_CENTS) * ROUND_TO_CENTS;
    return Math.max(DEFAULT_MAX_MONTHLY_CENTS, headroom);
}

const YEAR_STEP = 1;
const MIN_YEARS = 0;
const TARGET_AGE = 85;

function computeMaxYears(memberAge: number): number {
    const yearsTo85 = TARGET_AGE - memberAge;
    return Math.ceil(yearsTo85 / 5) * 5;
}

function formatProjectedDate(years: number): string {
    const target = new Date();
    target.setFullYear(target.getFullYear() + years);
    return target.toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
    });
}

const AccountRow = ({ account }: { account: InvestmentWaterfallAccount }) => {
    return (
        <XStack alignItems="center" width="100%" gap={16}>
            <View
                width={32}
                height={32}
                borderRadius={8}
                backgroundColor="$white"
                justifyContent="center"
                alignItems="center"
            >
                <Icon
                    icon={FruitfulLogoIcon}
                    size={16}
                    color={
                        account.accountType === 'brokerage'
                            ? '$leafDark'
                            : '$petalDark'
                    }
                />
            </View>

            <YStack gap={4} flex={1}>
                <BodySmallRegular>{account.label}</BodySmallRegular>
                <LabelRegular color="$gray1000">
                    {account.memberLabel}
                </LabelRegular>
            </YStack>

            <YStack gap={4} alignSelf="flex-end">
                <BodySmallEmphasized textAlign="right">
                    {formatDollars(account.projectedBalanceCents / 100, false)}
                </BodySmallEmphasized>
                <LabelRegular color="$gray1000" textAlign="right">
                    {formatDollars(account.annualAllocationCents / 100, false)}
                    /yr
                </LabelRegular>
            </YStack>
        </XStack>
    );
};

const AccountsList = ({
    accounts,
}: {
    accounts: InvestmentWaterfallAccount[];
}) => (
    <YStack
        width="100%"
        backgroundColor="rgba(255, 255, 255, 0.5)"
        borderRadius={16}
        borderWidth={1}
        borderColor="$white"
        overflow="hidden"
        {...Shadows.SMALL}
    >
        <View
            paddingVertical={8}
            paddingHorizontal={16}
            backgroundColor="rgba(255, 255, 255, 0.50);"
        >
            <BodyMediumRegular>Investment accounts</BodyMediumRegular>
        </View>
        <YStack padding={16} gap={16}>
            {accounts.map((account, index) => (
                <AccountRow account={account} key={index} />
            ))}
        </YStack>
    </YStack>
);

const ExistingIraRow = ({ account }: { account: ExistingIraAccount }) => {
    return (
        <XStack alignItems="center" width="100%" gap={16}>
            <View
                width={32}
                height={32}
                borderRadius={8}
                backgroundColor="$white"
                justifyContent="center"
                alignItems="center"
            >
                <Icon icon={FruitfulLogoIcon} size={16} color="$petalDark" />
            </View>

            <YStack gap={4} flex={1}>
                <BodySmallRegular>{account.label}</BodySmallRegular>
                <LabelRegular color="$gray1000">
                    {account.accountTypeLabel}
                </LabelRegular>
            </YStack>

            <YStack gap={4} alignSelf="flex-end">
                <BodySmallEmphasized textAlign="right">
                    {formatDollars(account.projectedBalanceCents / 100, false)}
                </BodySmallEmphasized>
                <LabelRegular color="$gray1000" textAlign="right">
                    Starting balance{' '}
                    {formatDollars(account.startingBalanceCents / 100, false)}
                </LabelRegular>
            </YStack>
        </XStack>
    );
};

const ExistingIraAccountsList = ({
    accounts,
}: {
    accounts: ExistingIraAccount[];
}) => {
    if (accounts.length === 0) return null;

    return (
        <YStack
            width="100%"
            backgroundColor="rgba(255, 255, 255, 0.5)"
            borderRadius={16}
            borderWidth={1}
            borderColor="$white"
            overflow="hidden"
            {...Shadows.SMALL}
        >
            <View
                paddingVertical={8}
                paddingHorizontal={16}
                backgroundColor="rgba(255, 255, 255, 0.50);"
            >
                <BodyMediumRegular>
                    Accounts moving to Fruitful
                </BodyMediumRegular>
            </View>
            <YStack padding={16} gap={16}>
                {accounts.map((account) => (
                    <ExistingIraRow account={account} key={account.id} />
                ))}
            </YStack>
        </YStack>
    );
};

const ContributionSlider = ({
    monthlyCents,
    maxMonthlyCents,
    onChange,
    onSlideEnd,
}: {
    monthlyCents: number;
    maxMonthlyCents: number;
    onChange: (value: number) => void;
    onSlideEnd: (value: number) => void;
}) => {
    const sliderValue = useMemo(() => [monthlyCents], [monthlyCents]);

    const roundToStep = useCallback(
        (value: number) =>
            Math.round(value / MONTHLY_CONTRIBUTION_STEP_CENTS) *
            MONTHLY_CONTRIBUTION_STEP_CENTS,
        [],
    );

    return (
        <YStack gap={16} paddingVertical={24} paddingHorizontal={16}>
            <YStack width="100%" gap={8}>
                <SuperscriptRegular
                    lineHeight={10}
                    textTransform="uppercase"
                    fontWeight={600}
                    letterSpacing="0.5px"
                >
                    Monthly contribution
                </SuperscriptRegular>
                <H5Emphasized
                    lineHeight={24}
                    style={{ fontFamily: FontFamilyName.SourceSerifRegular }}
                >
                    {formatDollars(monthlyCents / 100, false)}
                </H5Emphasized>
            </YStack>
            <Slider
                value={sliderValue}
                onSlideMove={(_, slideValue: number) => {
                    onChange(roundToStep(slideValue));
                }}
                onSlideEnd={() => {
                    onSlideEnd(roundToStep(monthlyCents));
                }}
                min={MONTHLY_CONTRIBUTION_STEP_CENTS}
                max={maxMonthlyCents}
                step={MONTHLY_CONTRIBUTION_STEP_CENTS}
                width="100%"
                trackActiveColor="$green800"
                trackColor="$gray400"
                thumbSize={24}
            />
        </YStack>
    );
};

const TimeHorizonSlider = ({
    years,
    maxYears,
    onChange,
    onSlideEnd,
}: {
    years: number;
    maxYears: number;
    onChange: (value: number) => void;
    onSlideEnd: (value: number) => void;
}) => {
    const sliderValue = useMemo(() => [years], [years]);

    return (
        <YStack gap={16} paddingVertical={24} paddingHorizontal={16}>
            <YStack width="100%" gap={8}>
                <SuperscriptRegular
                    lineHeight={10}
                    textTransform="uppercase"
                    fontWeight={600}
                    letterSpacing="0.5px"
                >
                    Date
                </SuperscriptRegular>
                <H5Emphasized
                    lineHeight={24}
                    style={{ fontFamily: FontFamilyName.SourceSerifRegular }}
                >
                    {years === 0 ? 'Today' : formatProjectedDate(years)}
                </H5Emphasized>
            </YStack>
            <YStack width="100%" gap={8}>
                <Slider
                    value={sliderValue}
                    onSlideMove={(_, slideValue: number) => {
                        onChange(Math.round(slideValue));
                    }}
                    onSlideEnd={() => {
                        onSlideEnd(Math.round(years));
                    }}
                    min={MIN_YEARS}
                    max={maxYears}
                    step={YEAR_STEP}
                    width="100%"
                    trackActiveColor="$green800"
                    trackColor="$gray400"
                    thumbSize={24}
                />
                <XStack width="100%" justifyContent="space-between">
                    <LabelRegular color="$gray1000">Today</LabelRegular>
                    <LabelRegular color="$gray1000">
                        {maxYears} years
                    </LabelRegular>
                </XStack>
            </YStack>
        </YStack>
    );
};

const ProjectionSummary = ({
    waterfall,
    monthlyCents,
}: {
    waterfall: InvestmentWaterfallResult;
    monthlyCents: number;
}) => {
    const totalContributed = monthlyCents * 12 * waterfall.projectedYears;
    const startingBalanceCents =
        waterfall.startingBalanceCents +
        waterfall.plannedStartingCashContributionCents;
    const growthCents =
        waterfall.projectedTotalCents - startingBalanceCents - totalContributed;

    return (
        <YStack gap={8} paddingVertical={24} paddingHorizontal={16}>
            <LabelRegular textTransform="uppercase">total balance</LabelRegular>
            <Text
                fontSize={40}
                lineHeight={40}
                letterSpacing="-2px"
                style={{ fontFamily: FontFamilyName.SourceSerifRegular }}
            >
                {formatDollars(waterfall.projectedTotalCents / 100, false)}
            </Text>
            <YStack width="100%" gap={4}>
                <BodySmallRegular>
                    Starting balance:{' '}
                    {formatDollars(startingBalanceCents / 100, false)}
                </BodySmallRegular>
                <BodySmallRegular>
                    Contributions:{' '}
                    {formatDollars(totalContributed / 100, false)}
                </BodySmallRegular>
                <BodySmallRegular>
                    Growth:{' '}
                    {formatDollars(Math.max(0, growthCents) / 100, false)}
                </BodySmallRegular>
            </YStack>
        </YStack>
    );
};

const InvestmentWaterfallSlide = () => {
    const { results, setResults, onTrackEvent } = useConversationalMMContext();
    const investment = results.rawChatResults.investment;

    const maxMonthlyCents = useMemo(() => {
        if (investment == null) return computeMaxMonthlyCents(null);
        return computeMaxMonthlyCents(
            resolveDefaultAnnualContributionCents(investment),
        );
    }, [investment]);

    const maxYears = useMemo(() => {
        const primaryAge = investment?.members[0]?.age ?? DEFAULT_MEMBER_AGE;
        return computeMaxYears(primaryAge);
    }, [investment]);

    const monthlyCents = useMemo(() => {
        if (investment == null) return 50_000;
        return Math.round(investment.annualContributionCents / 12);
    }, [investment]);

    const years = investment?.projectedYears ?? DEFAULT_PROJECTION_YEARS;

    // Write slider values back to the shared results so other slides
    // (e.g. WhyInvestSlide fee comparison) reflect the adjusted inputs.
    const updateInvestment = useCallback(
        (patch: { monthlyCents?: number; years?: number }) => {
            setResults((prev) => {
                if (prev.rawChatResults.investment == null) return prev;
                return {
                    ...prev,
                    rawChatResults: {
                        ...prev.rawChatResults,
                        investment: {
                            ...prev.rawChatResults.investment,
                            ...(patch.monthlyCents != null && {
                                annualContributionCents:
                                    patch.monthlyCents * 12,
                            }),
                            ...(patch.years != null && {
                                projectedYears: patch.years,
                            }),
                        },
                    },
                };
            });
        },
        [setResults],
    );

    const handleMonthlyChange = useCallback(
        (value: number) => {
            updateInvestment({ monthlyCents: value });
        },
        [updateInvestment],
    );

    const handleYearsChange = useCallback(
        (value: number) => {
            updateInvestment({ years: value });
        },
        [updateInvestment],
    );

    const handleMonthlySlideEnd = useCallback(
        (value: number) => {
            updateInvestment({ monthlyCents: value });
            onTrackEvent(ConversationalMMAnalyticsEvent.InvestContribSlider, {
                monthlyContributionCents: value,
            });
        },
        [updateInvestment, onTrackEvent],
    );

    const handleYearsSlideEnd = useCallback(
        (value: number) => {
            updateInvestment({ years: value });
            onTrackEvent(ConversationalMMAnalyticsEvent.InvestTimeSlider, {
                projectedYears: value,
            });
        },
        [updateInvestment, onTrackEvent],
    );

    // If contribution was persisted as $0 but pre-tax income is present,
    // re-apply the 15% default (e.g. stale results from a bad init).
    useEffect(() => {
        if (investment == null) return;
        if (investment.annualContributionCents > 0) return;

        const reinited = initIntakeInvestment(investment);
        if (reinited.annualContributionCents <= 0) return;

        setResults((prev) => {
            if (prev.rawChatResults.investment == null) return prev;
            return {
                ...prev,
                rawChatResults: {
                    ...prev.rawChatResults,
                    investment: reinited,
                },
            };
        });
    }, [investment, setResults]);

    const waterfall: InvestmentWaterfallResult | null = useMemo(() => {
        if (investment == null) return null;
        return buildInvestmentWaterfall(investment);
    }, [investment]);

    // Persist the computed waterfall recommendation (backdoor vs Roth, etc.) —
    // same pattern as goalCompletionDates for debt/savings branches.
    useEffect(() => {
        if (waterfall == null) return;
        const persisted = toPersistedInvestmentWaterfall(waterfall);
        setResults((prev) => ({
            ...prev,
            investmentWaterfall: persisted,
        }));
    }, [waterfall, setResults]);

    if (investment == null || waterfall == null) {
        return null;
    }

    return (
        <YStack gap={24}>
            <LinearGradientBackgroundCard>
                <YStack
                    gap={24}
                    width="100%"
                    justifyContent="center"
                    alignItems="center"
                >
                    <Header
                        title="Investment waterfall"
                        description="Your money system funds your investments automatically, maximizing contributions to each account in priority order — so your money is always working toward what matters most."
                    />

                    <YStack
                        width="100%"
                        backgroundColor="rgba(255, 255, 255, 0.5)"
                        borderRadius={16}
                        borderWidth={1}
                        borderColor="$white"
                        {...Shadows.SMALL}
                    >
                        <ContributionSlider
                            monthlyCents={monthlyCents}
                            maxMonthlyCents={maxMonthlyCents}
                            onChange={handleMonthlyChange}
                            onSlideEnd={handleMonthlySlideEnd}
                        />
                        <View
                            width="100%"
                            height={1}
                            backgroundColor="$white"
                        />
                        <TimeHorizonSlider
                            years={years}
                            maxYears={maxYears}
                            onChange={handleYearsChange}
                            onSlideEnd={handleYearsSlideEnd}
                        />
                        <View
                            width="100%"
                            height={1}
                            backgroundColor="$white"
                        />
                        <ProjectionSummary
                            waterfall={waterfall}
                            monthlyCents={monthlyCents}
                        />
                    </YStack>

                    <AccountsList accounts={waterfall.accounts} />

                    <ExistingIraAccountsList
                        accounts={waterfall.existingIraAccounts}
                    />

                    <LabelRegular color="$gray1000">
                        Calculation assumes an annualized rate of return of{' '}
                        {INVESTMENT_RETURN_PERCENT}% and annual contributions of{' '}
                        {formatDollars((monthlyCents / 100) * 12, false)}. An
                        investor may have outcomes that are different than what
                        is displayed. All investments have risk.
                    </LabelRegular>

                    <SlideNextButton />
                </YStack>
            </LinearGradientBackgroundCard>

            <InvestingDisclosures />
        </YStack>
    );
};

export default InvestmentWaterfallSlide;
